/**
 * Thin fetch wrapper around `GET/POST/PATCH/DELETE /v1/cart*`. Mirrors
 * `modules/purchase/api.ts`'s auth handling exactly (dev-bypass token,
 * force-refresh-on-401 retry) rather than the simpler pattern in
 * `modules/entitlements/api.ts` — Cart mutations lead directly into
 * Checkout, a money-moving surface, so the same discipline applies.
 */
import { API_BASE } from '@/src/infrastructure/lib/api';
import { auth } from '@/src/infrastructure/config/firebase';
import { logout } from '@/src/modules/auth/session';
import type { AddItemRequest, Cart, CartCheckoutFailure, CheckoutResult } from './types';
import { MAX_UNITS_PER_CHECKOUT } from './constants';

/** Unknown/inactive collection code, or a checkout line that failed validation — 422. Always a fixed, customer-safe message; the raw backend body is never shown (matches PurchaseRejectedError's precedent). */
export class CartRejectedError extends Error {
  constructor(message = 'That item can’t be added right now.') {
    super(message);
    this.name = 'CartRejectedError';
  }
}

/**
 * A 422 with no `failures` array carries a stable `error` code
 * (CartExceptionFilter's `exception.code`) — map the ones a retry can never
 * fix on its own to a specific, actionable message, rather than the generic
 * `CartRejectedError` default which (caught in code review) previously told
 * every such failure to "try again," including cases where retrying is
 * deterministically impossible (an over-cap cart, an empty cart) until the
 * user actually changes something.
 */
const KNOWN_CART_ERROR_MESSAGES: Record<string, string> = {
  CART_TOO_LARGE_FOR_CHECKOUT: `Your cart has more than ${MAX_UNITS_PER_CHECKOUT} items total — remove some to check out.`,
  CART_EMPTY: 'Your cart is empty — add an item before checking out.',
};

/** Cart checkout rejected some or all lines — carries the per-line failures so the UI can point at exactly which item to fix. */
export class CartCheckoutRejectedError extends Error {
  readonly failures: CartCheckoutFailure[];
  constructor(failures: CartCheckoutFailure[]) {
    super('One or more items in your cart could not be checked out.');
    this.name = 'CartCheckoutRejectedError';
    this.failures = failures;
  }
}

/** The active cart was converted/expired, or was modified concurrently — 409/404. The caller should re-fetch the cart and retry. */
export class CartStateChangedError extends Error {
  constructor(message = 'Your cart changed — please try again.') {
    super(message);
    this.name = 'CartStateChangedError';
  }
}

async function authFetch<T>(url: string, options: RequestInit = {}, retried = false): Promise<T> {
  const isBypass = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';
  const token = isBypass ? 'dev-token' : await auth.currentUser?.getIdToken(retried).catch(() => null);
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers as Record<string, string> | undefined),
    },
  });

  if (res.status === 401 && !retried && !isBypass && auth.currentUser) {
    return authFetch<T>(url, options, true);
  }
  if (res.status === 401) {
    if (isBypass) {
      throw new Error('Unauthorized: ensure backend is also running with DEV_AUTH_BYPASS=true');
    }
    await logout().catch(() => undefined);
    window.location.href = '/login';
    throw new Error('Session expired');
  }
  if (res.status === 404 || res.status === 409) {
    throw new CartStateChangedError();
  }
  if (res.status === 422) {
    const body = await res.json().catch(() => ({}));
    if (Array.isArray(body?.failures)) {
      throw new CartCheckoutRejectedError(body.failures as CartCheckoutFailure[]);
    }
    const knownMessage = typeof body?.error === 'string' ? KNOWN_CART_ERROR_MESSAGES[body.error] : undefined;
    throw new CartRejectedError(knownMessage);
  }
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  const body = await res.json();
  return (body?.data !== undefined ? body.data : body) as T;
}

export function getCart(): Promise<Cart> {
  return authFetch<Cart>(`${API_BASE}/cart`);
}

export function addItem(request: AddItemRequest): Promise<Cart> {
  return authFetch<Cart>(`${API_BASE}/cart/items`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export function updateItemQuantity(cartItemId: string, quantity: number): Promise<Cart> {
  return authFetch<Cart>(`${API_BASE}/cart/items/${encodeURIComponent(cartItemId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ quantity }),
  });
}

export function removeItem(cartItemId: string): Promise<Cart> {
  return authFetch<Cart>(`${API_BASE}/cart/items/${encodeURIComponent(cartItemId)}`, {
    method: 'DELETE',
  });
}

export function clearCart(): Promise<Cart> {
  return authFetch<Cart>(`${API_BASE}/cart/items`, { method: 'DELETE' });
}

/** Coupons are not supported at Cart checkout — see the backend's CheckoutCartCommand docstring for why. */
export function checkoutCart(): Promise<CheckoutResult> {
  return authFetch<CheckoutResult>(`${API_BASE}/cart/checkout`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}
